'use client';

import { CalendarBlank } from '@phosphor-icons/react/ssr';
import {
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  useId,
  useRef,
  useState,
} from 'react';
import {
  dateMatchModifiers,
  type Matcher,
  rangeContainsModifiers,
} from 'react-day-picker';

import { Input } from '../ui/input';
import { Popover, PopoverAnchor, PopoverContent } from '../ui/popover';
import {
  DATE_INPUT_MESSAGES,
  type DateInputError,
  type DateRange,
  formatDate,
  formatDateRange,
  parseDateInput,
  parseDateRangeInput,
} from './date-format';
import { DatePickerContent } from './date-picker';
import { pickerFieldStyles } from './date-time-styles';
import { useFieldControl, useFieldLabelId } from './field';

/** Figma "Type": one date or a start–end range. `null` = controlled and empty. */
export type DateFieldSelection =
  | {
      type?: 'single';
      value?: Date | null;
      defaultValue?: Date;
      onValueChange?: (value: Date | undefined) => void;
    }
  | {
      type: 'range';
      value?: DateRange | null;
      defaultValue?: DateRange;
      onValueChange?: (value: DateRange | undefined) => void;
    };

export type DateFieldProps = DateFieldSelection & {
  /** Defaults to "Select a date" / "Select dates" (Figma). */
  placeholder?: string;
  /** Days that can't be picked or typed, e.g. `{ before: today }`. */
  disabledDays?: Matcher | Matcher[];
  /** Override "today" (tests, other timezones). */
  today?: Date;
  startMonth?: Date;
  endMonth?: Date;
  /**
   * Typed text failed validation on blur / Enter (or passed again: `null`).
   * Show `message` as the Field `error` - "That date doesn’t exist".
   */
  onInputError?: (message: string | null, reason: DateInputError | null) => void;
  disabled?: boolean;
  id?: string;
  name?: string;
  /** Needed when not inside a Field. */
  'aria-label'?: string;
  /** Error state without a Field (inside a Field, `error` sets it). */
  'aria-invalid'?: boolean;
  /** Class for the box (width, margins). */
  className?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultOpen?: boolean;
};

const display = (type: 'single' | 'range', value: Date | DateRange | undefined) =>
  !value
    ? ''
    : type === 'range'
      ? formatDateRange(value as DateRange)
      : formatDate(value as Date);

/**
 * Typed or picked dates (Figma: Date & Time ↳ Date Field). A Text-Input-style
 * box: type "23 Sep 2026" or "2026-09-23" (ranges "14 – 20 Sep 2026"), or
 * click / press ↓ for the Date Picker 4px below. Typed text is validated on
 * blur or Enter; a bad date keeps the text, marks the box invalid and calls
 * `onInputError` with the reason for the Field. The input is a combobox
 * with a dialog popup; ↓ moves focus into the calendar, Escape returns it.
 */
export function DateField(props: DateFieldProps) {
  const {
    placeholder,
    disabledDays,
    today,
    startMonth,
    endMonth,
    onInputError,
    id,
    name,
    'aria-label': ariaLabel,
    'aria-invalid': ariaInvalid,
    className,
    open: openProp,
    onOpenChange,
    defaultOpen = false,
  } = props;
  const type = props.type ?? 'single';
  const [inner, setInner] = useState<Date | DateRange | undefined>(props.defaultValue);
  const value = props.value === undefined ? inner : (props.value ?? undefined);
  const shown = display(type, value);

  const [text, setText] = useState(shown);
  const [error, setError] = useState<DateInputError | null>(null);
  // A new value (picked, or from outside) replaces the text and any error.
  const [prevShown, setPrevShown] = useState(shown);
  if (shown !== prevShown) {
    setPrevShown(shown);
    setText(shown);
    setError(null);
  }

  const [openState, setOpenState] = useState(defaultOpen);
  const open = openProp ?? openState;
  const setOpen = (next: boolean) => {
    setOpenState(next);
    onOpenChange?.(next);
  };

  const inputRef = useRef<HTMLInputElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const focusCalendar = useRef(false);
  const contentId = useId();
  const fieldLabelId = useFieldLabelId();
  const { disabled, ...control } = useFieldControl({
    id,
    disabled: props.disabled,
    'aria-invalid': ariaInvalid || Boolean(error) || undefined,
  });
  const styles = pickerFieldStyles();

  const report = (next: DateInputError | null) => {
    setError(next);
    onInputError?.(next && DATE_INPUT_MESSAGES[next], next);
  };

  const change = (next: Date | DateRange | undefined) => {
    setInner(next);
    if (type === 'range') props.onValueChange?.(next as DateRange & Date);
    else props.onValueChange?.(next as Date & DateRange);
    // Same value typed in another format: normalise the text.
    setText(display(type, next));
    report(null);
  };

  /** Validate typed text: empty clears, else parse + check unavailable days. */
  const commit = () => {
    const typed = text.trim();
    if (typed === shown) return;
    if (!typed) return change(undefined);
    const parsed = type === 'range' ? parseDateRangeInput(typed) : parseDateInput(typed);
    if (!parsed.ok) return report(parsed.error);
    const unavailable =
      disabledDays !== undefined &&
      (parsed.value instanceof Date
        ? dateMatchModifiers(parsed.value, disabledDays)
        : rangeContainsModifiers(parsed.value, disabledDays));
    if (unavailable) return report('unavailable');
    change(parsed.value);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      focusCalendar.current = true;
      if (open) focusDay();
      else setOpen(true);
    } else if (event.key === 'Enter') {
      commit();
      if (open) setOpen(false);
    } else if (event.key === 'Escape' && open) {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === 'Tab' && open) {
      setOpen(false);
    }
  };

  /** Focus the calendar's tab stop: the selected day, else today, else the 1st. */
  const focusDay = () =>
    document
      .getElementById(contentId)
      ?.querySelector<HTMLElement>('[data-slot=day-cell][tabindex="0"]')
      ?.focus();

  const openFromPointer = (event: ReactPointerEvent) => {
    if (disabled || event.button !== 0) return;
    focusCalendar.current = false;
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    inputRef.current?.focus();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div
          ref={boxRef}
          data-slot="date-field"
          data-open={open || undefined}
          className={styles.root({ className })}
          onPointerDown={openFromPointer}
        >
          <CalendarBlank aria-hidden className={styles.icon()} />
          <Input
            ref={inputRef}
            {...control}
            disabled={disabled}
            name={name}
            role="combobox"
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-controls={open ? contentId : undefined}
            aria-autocomplete="none"
            aria-label={ariaLabel}
            autoComplete="off"
            placeholder={
              placeholder ?? (type === 'range' ? 'Select dates' : 'Select a date')
            }
            className={styles.input()}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onBlur={commit}
            onKeyDown={onKeyDown}
          />
        </div>
      </PopoverAnchor>
      <PopoverContent
        id={contentId}
        aria-labelledby={fieldLabelId}
        aria-label={
          fieldLabelId ? undefined : type === 'range' ? 'Choose dates' : 'Choose a date'
        }
        className="min-w-0 p-3"
        onOpenAutoFocus={(e) => {
          // Pointer: typing stays possible. ↓: into the calendar.
          e.preventDefault();
          if (focusCalendar.current) focusDay();
        }}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          close();
        }}
        onInteractOutside={(e) => {
          // The box itself toggles nothing - it only opens.
          if (boxRef.current?.contains(e.target as Node)) e.preventDefault();
        }}
      >
        {type === 'range' ? (
          <DatePickerContent
            type="range"
            value={value as DateRange | undefined}
            onValueChange={(v) => {
              change(v);
              close();
            }}
            onCancel={close}
            disabled={disabledDays}
            today={today}
            startMonth={startMonth}
            endMonth={endMonth}
          />
        ) : (
          <DatePickerContent
            value={value as Date | undefined}
            onValueChange={(v) => {
              change(v);
              close();
            }}
            disabled={disabledDays}
            today={today}
            startMonth={startMonth}
            endMonth={endMonth}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}
